import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
} from "recharts";

function Kpi({ label, value, sub }) {
    return (
        <div className="p-4 bg-card border border-border rounded-md">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-1">
                {label}
            </div>
            <div className="text-2xl font-semibold tracking-tight">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
    );
}

export default function Analytics() {
    const [kpi, setKpi] = useState(null);
    useEffect(() => {
        api.get("/analytics/kpi").then((r) => setKpi(r.data));
    }, []);

    if (!kpi)
        return <div className="text-sm text-muted-foreground">Loading analytics…</div>;

    return (
        <div className="space-y-5" data-testid="analytics-page">
            <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <Kpi label="Today" value={kpi.total_today} />
                <Kpi label="Week" value={kpi.total_week} />
                <Kpi label="Month" value={kpi.total_month} />
                <Kpi label="All-time" value={kpi.total_all} />
                <Kpi label="Open" value={kpi.open_count} />
                <Kpi label="Closed" value={kpi.closed_count} />
                <Kpi label="Escalated" value={kpi.escalated_count} />
                <Kpi label="Satisfaction" value={`${kpi.satisfaction_percent}%`} />
                <Kpi label="Paid / today" value={formatMoney(kpi.paid_revenue_today)} />
                <Kpi label="Paid / week" value={formatMoney(kpi.paid_revenue_week)} />
                <Kpi label="Paid / month" value={formatMoney(kpi.paid_revenue_month)} />
                <Kpi label="Avg resolution" value={`${kpi.avg_resolution_minutes_overall}m`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title="SLA success rate per tier">
                    <div className="grid grid-cols-3 gap-3">
                        {["IMMEDIATE", "PRIORITY", "STANDARD"].map((t) => (
                            <div key={t} className="p-3 bg-background border border-border rounded-md text-center">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                                    {t}
                                </div>
                                <div className="text-2xl font-semibold mt-1">
                                    {kpi.sla_success_by_tier[t]}%
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    avg {kpi.avg_resolution_minutes_by_tier[t]}m
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
                <Card title="Tickets by category">
                    <Chart data={kpi.by_category} fill="#00E676" />
                </Card>
                <Card title="Tickets by agent">
                    <Chart data={kpi.by_agent} fill="#60A5FA" />
                </Card>
            </div>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="p-4 bg-card border border-border rounded-md">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-3">
                {title}
            </div>
            {children}
        </div>
    );
}

function Chart({ data, fill }) {
    if (!data?.length) {
        return (
            <div className="text-sm text-muted-foreground h-40 flex items-center justify-center">
                No data yet.
            </div>
        );
    }
    return (
        <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: -10 }}>
                    <CartesianGrid stroke="#27272A" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#A1A1AA" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#A1A1AA" }} allowDecimals={false} />
                    <Tooltip
                        contentStyle={{
                            background: "#121214",
                            border: "1px solid #27272A",
                            borderRadius: 6,
                            fontSize: 12,
                        }}
                    />
                    <Bar dataKey="count" fill={fill} radius={[3, 3, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
